/**
 * Sprint 2 Fase 2 — Trackingstatus Presets CMS beheer.
 *
 * Beheerpagina voor herbruikbare trackingstatus-templates.
 * Mount: Instellingen → Trackingpresets (alleen Owner/Admin/Manager).
 *
 * Acties:
 *  - Nieuwe preset toevoegen
 *  - Bewerken (inline rij → modal)
 *  - Deactiveren / activeren (snel via toggle in tabel)
 *  - Verwijderen (soft-delete als al gebruikt)
 *  - Inactive presets tonen via toggle bovenaan
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Save, X, Loader2, Eye, EyeOff,
  Sparkles, AlertTriangle,
} from "lucide-react";

import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../ui/dialog";

import { adminApi, formatApiError } from "../../../context/AuthContext";

const EMPTY_FORM = {
  name: "",
  code: "",
  public_label: "",
  public_label_en: "",
  public_message: "",
  public_message_en: "",
  progress_percent: 50,
  active: true,
  sort_order: 100,
};

function slugifyCode(input) {
  return (input || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9_\- ]/g, "")
    .trim()
    .replace(/\s+/g, "_")
    .slice(0, 80);
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("nl-NL", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export default function TrackingPresetsSection() {
  const [items, setItems] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async (showInactive = includeInactive) => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/tracking-status-presets", {
        params: { include_inactive: showInactive },
      });
      setItems(r.data?.items || []);
    } catch (err) {
      toast.error(formatApiError(err) || "Presets laden mislukt");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await adminApi.get("/admin/tracking-status-presets", {
          params: { include_inactive: includeInactive },
        });
        if (!cancelled) {
          setItems(r.data?.items || []);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(formatApiError(err) || "Presets laden mislukt");
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [includeInactive]);

  const handleCreate = () => setEditing({ ...EMPTY_FORM, __mode: "create" });
  const handleEdit = (p) => setEditing({ ...p, __mode: "edit" });

  const toggleActive = async (p) => {
    try {
      await adminApi.patch(`/admin/tracking-status-presets/${p.id}`, {
        active: !p.active,
      });
      toast.success(p.active ? "Preset gedeactiveerd" : "Preset geactiveerd");
      load(includeInactive);
    } catch (err) {
      toast.error(formatApiError(err) || "Wijzigen mislukt");
    }
  };

  const handleDelete = async (p) => {
    const willSoftDelete = (p.used_count || 0) > 0;
    const msg = willSoftDelete
      ? `Deze preset is ${p.used_count}× toegepast. Hij wordt gearchiveerd (soft-delete) zodat bestaande labels intact blijven. Doorgaan?`
      : `Preset "${p.name}" definitief verwijderen?`;
    if (!window.confirm(msg)) return;
    try {
      await adminApi.delete(`/admin/tracking-status-presets/${p.id}`);
      toast.success(willSoftDelete ? "Preset gearchiveerd" : "Preset verwijderd");
      load(includeInactive);
    } catch (err) {
      toast.error(formatApiError(err) || "Verwijderen mislukt");
    }
  };

  return (
    <div data-testid="tracking-presets-section">
      <div className="bg-white border border-gray-200 p-4 mb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <h2 className="font-chivo font-bold text-lg text-[#082c50] flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Trackingstatus-presets
            </h2>
            <p className="text-xs text-gray-600 mt-1">
              Standaard 5999Cargo-statussen die medewerkers met één klik kunnen toepassen op een shipping label.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                data-testid="presets-show-inactive"
              />
              Inactieve tonen
            </label>
            <Button
              size="sm"
              onClick={handleCreate}
              className="bg-[#082c50] text-white rounded-md text-xs"
              data-testid="presets-new-btn"
            >
              <Plus className="w-3 h-3 mr-1" /> Nieuwe preset
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b border-gray-200 text-gray-600 uppercase font-bold">
            <tr>
              <th className="text-left p-2">#</th>
              <th className="text-left p-2">Naam</th>
              <th className="text-left p-2">Code</th>
              <th className="text-left p-2">Label NL</th>
              <th className="text-left p-2">Label EN</th>
              <th className="text-left p-2">Progress</th>
              <th className="text-center p-2">Actief</th>
              <th className="text-left p-2">Gebruikt</th>
              <th className="text-left p-2">Bijgewerkt</th>
              <th className="text-right p-2">Acties</th>
            </tr>
          </thead>
          <tbody data-testid="presets-table-body">
            {loading ? (
              <tr><td colSpan={10} className="p-8 text-center text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Laden…
              </td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={10} className="p-8 text-center text-gray-500" data-testid="presets-empty">
                Geen presets gevonden.
              </td></tr>
            ) : (
              items.map((p) => (
                <tr
                  key={p.id}
                  className={`border-b border-gray-100 ${p.active ? "" : "bg-gray-50/60 text-gray-500"}`}
                  data-testid={`preset-row-${p.code}`}
                >
                  <td className="p-2 text-gray-500">{p.sort_order}</td>
                  <td className="p-2 font-bold text-[#082c50]">{p.name}</td>
                  <td className="p-2 font-mono text-[10px]">{p.code}</td>
                  <td className="p-2">{p.public_label}</td>
                  <td className="p-2">{p.public_label_en || <span className="text-gray-400">—</span>}</td>
                  <td className="p-2">{p.progress_percent}%</td>
                  <td className="p-2 text-center">
                    <button
                      type="button"
                      onClick={() => toggleActive(p)}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                        p.active
                          ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                          : "bg-gray-100 text-gray-600 border-gray-300"
                      }`}
                      data-testid={`preset-toggle-active-${p.code}`}
                      title={p.active ? "Deactiveren" : "Activeren"}
                    >
                      {p.active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {p.active ? "Actief" : "Inactief"}
                    </button>
                  </td>
                  <td className="p-2 text-gray-600">{p.used_count || 0}×</td>
                  <td className="p-2 text-gray-500 text-[10px]">
                    {fmtDate(p.updated_at)}<br />
                    <span className="text-gray-400">{p.updated_by_email || "—"}</span>
                  </td>
                  <td className="p-2 text-right">
                    <div className="inline-flex gap-1">
                      <Button
                        size="sm" variant="outline"
                        onClick={() => handleEdit(p)}
                        className="rounded-md text-[10px] h-7 px-2"
                        data-testid={`preset-edit-${p.code}`}
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => handleDelete(p)}
                        className="rounded-md text-[10px] h-7 px-2 border-red-200 text-red-600 hover:bg-red-50"
                        data-testid={`preset-delete-${p.code}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <PresetEditDialog
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load(includeInactive);
          }}
        />
      )}
    </div>
  );
}

function PresetEditDialog({ initial, onClose, onSaved }) {
  const isCreate = initial.__mode === "create";
  const [form, setForm] = useState(() => ({
    name: initial.name || "",
    code: initial.code || "",
    public_label: initial.public_label || "",
    public_label_en: initial.public_label_en || "",
    public_message: initial.public_message || "",
    public_message_en: initial.public_message_en || "",
    progress_percent: typeof initial.progress_percent === "number" ? initial.progress_percent : 50,
    active: initial.active !== false,
    sort_order: typeof initial.sort_order === "number" ? initial.sort_order : 100,
  }));
  const [busy, setBusy] = useState(false);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const valid = !!form.name.trim() && form.name.trim().length >= 2
    && !!form.code.trim() && !!form.public_label.trim()
    && form.progress_percent >= 0 && form.progress_percent <= 100;

  const handleSave = async () => {
    if (!valid) {
      toast.error("Naam (min 2), code en publiek label zijn verplicht.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        code: slugifyCode(form.code),
        public_label: form.public_label.trim(),
        public_label_en: form.public_label_en.trim() || null,
        public_message: form.public_message.trim() || null,
        public_message_en: form.public_message_en.trim() || null,
        progress_percent: Math.max(0, Math.min(100, Number(form.progress_percent))),
        active: !!form.active,
        sort_order: Math.max(0, Math.min(9999, Number(form.sort_order))),
      };
      if (isCreate) {
        await adminApi.post("/admin/tracking-status-presets", payload);
        toast.success("Preset aangemaakt");
      } else {
        await adminApi.patch(`/admin/tracking-status-presets/${initial.id}`, payload);
        toast.success("Preset bijgewerkt");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err) || "Opslaan mislukt");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="preset-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo font-bold text-lg text-[#082c50]">
            {isCreate ? "Nieuwe trackingstatus-preset" : `Preset bewerken — ${initial.name}`}
          </DialogTitle>
          <DialogDescription>
            Een snapshot van deze preset wordt naar het label gekopieerd zodra een medewerker
            hem toepast. Latere wijzigingen aan de preset breken bestaande labels nooit.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-2">
            <div>
              <Label className="text-[10px] uppercase font-bold text-gray-600">Naam *</Label>
              <Input
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="bv. In behandeling bij douane"
                className="rounded-md text-xs"
                data-testid="preset-form-name"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold text-gray-600">Code *</Label>
              <Input
                value={form.code}
                onChange={(e) => setField("code", slugifyCode(e.target.value))}
                placeholder="in_douane_behandeling"
                className="rounded-md text-xs font-mono"
                data-testid="preset-form-code"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase font-bold text-gray-600">Publiek label NL *</Label>
              <Input
                value={form.public_label}
                onChange={(e) => setField("public_label", e.target.value)}
                className="rounded-md text-xs"
                data-testid="preset-form-public-label-nl"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold text-gray-600">Publiek label EN</Label>
              <Input
                value={form.public_label_en}
                onChange={(e) => setField("public_label_en", e.target.value)}
                className="rounded-md text-xs"
                data-testid="preset-form-public-label-en"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase font-bold text-gray-600">Publieke boodschap NL</Label>
              <Textarea
                value={form.public_message}
                onChange={(e) => setField("public_message", e.target.value)}
                rows={3}
                className="rounded-md text-xs"
                data-testid="preset-form-public-message-nl"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold text-gray-600">Publieke boodschap EN</Label>
              <Textarea
                value={form.public_message_en}
                onChange={(e) => setField("public_message_en", e.target.value)}
                rows={3}
                className="rounded-md text-xs"
                data-testid="preset-form-public-message-en"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] uppercase font-bold text-gray-600">
                Voortgang ({form.progress_percent}%)
              </Label>
              <input
                type="range" min={0} max={100} step={5}
                value={form.progress_percent}
                onChange={(e) => setField("progress_percent", Number(e.target.value))}
                className="w-full mt-2 accent-[#082c50]"
                data-testid="preset-form-progress"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold text-gray-600">Volgorde</Label>
              <Input
                type="number" min={0} max={9999}
                value={form.sort_order}
                onChange={(e) => setField("sort_order", Number(e.target.value))}
                className="rounded-md text-xs"
                data-testid="preset-form-sort-order"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold text-gray-600">Status</Label>
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setField("active", e.target.checked)}
                  data-testid="preset-form-active"
                />
                <span className="text-xs font-bold">
                  {form.active ? "Actief" : "Inactief"}
                </span>
              </label>
            </div>
          </div>

          {/* Preview */}
          <div className="mt-3 rounded-md border border-dashed border-emerald-300 bg-emerald-50/40 p-3">
            <div className="text-[10px] uppercase font-bold text-emerald-800 mb-1">
              Voorvertoning klantweergave
            </div>
            <div className="bg-white border border-gray-200 rounded-md p-3">
              <div className="text-xs font-bold text-[#082c50]">
                {form.public_label || "—"}
              </div>
              {form.public_message && (
                <p className="text-xs text-gray-700 mt-1 leading-relaxed">
                  {form.public_message}
                </p>
              )}
              <div className="mt-2">
                <div className="flex items-center justify-between text-[10px] uppercase font-bold text-[#082c50] mb-1">
                  <span>Voortgang</span>
                  <span>{form.progress_percent}%</span>
                </div>
                <div className="w-full h-1.5 rounded-full bg-[#082c50]/10 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#1a5193] to-cyan-400 transition-all"
                    style={{ width: `${form.progress_percent}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="pt-3 gap-2">
          <Button
            variant="outline" onClick={onClose} disabled={busy}
            className="rounded-md text-xs"
            data-testid="preset-form-cancel"
          >
            <X className="w-3 h-3 mr-1" /> Annuleren
          </Button>
          <Button
            onClick={handleSave} disabled={busy || !valid}
            className="bg-[#082c50] text-white rounded-md text-xs"
            data-testid="preset-form-save"
          >
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
            {isCreate ? "Aanmaken" : "Opslaan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
